<script setup>
import Modal from "@/components/modal/Modal";
import {
    convertPriorityToColor, HospitalTypeLabel,
    HpoTransportDestinationRequestStatus,
    HpoTransportStatus,
    StartTriageColorLabel,
} from "../../choices";
import {priorityClass, ventilated} from "../../utils";
import {GenderLabel} from "../../../../choices";
import * as yup from "yup";
import {numberValidator, requiredMessage, stringValidator} from "../../../../utils/yupValidators";
import {HpoPatientTransportReplyMutation} from "@/graphql/mutations/hpo/patient/transportReply.graphql"
import {useMutation} from "@vue/apollo-composable";
import {useDjangoForm} from "../../../../utils/forms";

const props = defineProps({
    reply: {
        type: Number,
        required: true,
    },
    patient: {
        type: Object,
        required: true,
    },
})
const emit = defineEmits(['close'])


const {defineField, handleMutationSubmit, errors} = useDjangoForm({
    validationSchema: yup.object({
        status: numberValidator.required(requiredMessage).oneOf([
            HpoTransportDestinationRequestStatus.ACCEPTED,
            HpoTransportDestinationRequestStatus.REFUSED,
            HpoTransportDestinationRequestStatus.CANCELED,
            HpoTransportStatus.TERMINATED,
        ]),
        rejectionReason: yup.string().nullable().when('status', {
            is: (val) => [
                HpoTransportDestinationRequestStatus.REFUSED,
                HpoTransportDestinationRequestStatus.CANCELED,
                HpoTransportStatus.TERMINATED
            ].includes(val),
            then: () => stringValidator.required(requiredMessage)
        }),
    }),
    initialValues: {
        status: props.reply,
        rejectionReason: null
    }
});
const [status, statusProps] = defineField('status');
const [rejectionReason, rejectionReasonProps] = defineField('rejectionReason');


const {mutate} = useMutation(HpoPatientTransportReplyMutation)

var titleText
var reasonFieldLabel
var submitButtonText
if (props.reply === HpoTransportDestinationRequestStatus.ACCEPTED) {
    titleText = pgettext('title', 'Accept transport of patient')
    submitButtonText = pgettext('action', 'accept')
} else if (props.reply === HpoTransportDestinationRequestStatus.REFUSED) {
    titleText = pgettext('title', 'Reject transport of patient')
    reasonFieldLabel = pgettext('label', 'Reason of rejection')
    submitButtonText = pgettext('action', 'reject')
} else if (props.reply === HpoTransportDestinationRequestStatus.CANCELED) {
    titleText = pgettext('title', 'Cancel transport of patient')
    reasonFieldLabel = pgettext('label', 'Reason of canceling')
    submitButtonText = pgettext('action', 'cancel')
} else if (props.reply === HpoTransportStatus.TERMINATED) {
    titleText = pgettext('title', 'Terminate transport of patient')
    reasonFieldLabel = pgettext('label', 'Reason of termination')
    submitButtonText = pgettext('action', 'terminate')
}

function close() {
    emit('close')
}

const onSubmit = handleMutationSubmit((values) => {
    return mutate({
        patientId: props.patient.id,
        hospitalId: props.patient.hospital.id,
        ...values
    });
}, {
    onSuccess() {
        close();
    },
    onMutationError(error) {
        alert(error);
    }
});

</script>

<template>
    <Modal :title="titleText" @close="close" :closeOnClickOutside="false">
        <div class="hpo-transport-reply-body">
            <dl>
                <dt :class="priorityClass(patient.transportPriority)">{{ pgettext('label', 'Color') }}:</dt>
                <dd :class="priorityClass(patient.transportPriority)">
                    {{ StartTriageColorLabel[convertPriorityToColor(patient.transportPriority)].toUpperCase() }}
                </dd>
                <dt>{{ pgettext('label', 'TIC Card Number') }}:</dt>
                <dd>{{ patient.tic }}</dd>
                <dt>{{ pgettext('label', 'Gender') }}:</dt>
                <dd>{{ GenderLabel[patient.gender] }}</dd>
                <dt>{{ pgettext('label', 'Diagnose') }}:</dt>
                <dd>{{ patient.diagnose }}</dd>
                <dt>{{ pgettext('label', 'ALV') }}:</dt>
                <dd>{{ ventilated(patient.ventilated) }}</dd>
                <dt>{{ pgettext('label', 'Hospital Type') }}:</dt>
                <dd>{{ HospitalTypeLabel[patient.hospitalType] }}</dd>
                <dt>{{ pgettext('label', 'Hospital') }}:</dt>
                <dd>{{ patient.hospital.name }}</dd>
                <dt>{{ pgettext('label', 'Arrival in minutes') }}:</dt>
                <dd>{{ patient.transportMinutes }}</dd>
            </dl>

            <v-form @submit.prevent="onSubmit">
                <!-- Status -->
                <v-text-field hidden :value="status" v-bind="statusProps"></v-text-field>

                <!-- Reason -->
                <v-text-field
                    v-if="[HpoTransportDestinationRequestStatus.REFUSED, HpoTransportDestinationRequestStatus.CANCELED, HpoTransportStatus.TERMINATED].includes(reply)"
                    v-model="rejectionReason"
                    v-bind="rejectionReasonProps"
                    :label="reasonFieldLabel"
                ></v-text-field>

                <v-btn type="submit"
                       :text="submitButtonText"
                       block
                ></v-btn>
            </v-form>


        </div>
    </Modal>

</template>